import * as React from "react"
import { createRoot } from "react-dom/client"
import { Provider } from "react-redux"

import { AppStore } from "../../store"
import OldUIContext from "../../olduicontext"

import {
    initialize,
    requestAttachments,
    loadMoreAttachments as loadMoreAttachmentsAction,
} from "./actions"

import DetailPageInlineAttachment from "./components/DetailPageInlineAttachment"
import CNIInlineAttachment from "./components/CNIInlineAttachment"
import { IOldUIContext, IOldUIIPEField } from "../../global"

interface OldUIInit {
    makeDetailPageInlineAttachment: (
        itemId: number,
        customFieldDefinitionId: string,
        customFieldName: string,
        deleteAttachmentAllowed: boolean,
        addAttachmentAllowed: boolean,
        isPrintView: boolean,
    ) => void
    makeCNIInlineAttachment: (
        ipfId: string,
        ipfName: string,
        ipfValue: File[],
    ) => void
    updateElements: (values: IOldUIIPEField[]) => void
    loadMoreAttachments: (customFieldDefinitionId: string) => void
}

export const isInlineAttachmentValue = (
    itemId: number | undefined,
    fieldId: string,
    value: IOldUIIPEField,
): boolean => {
    if (!itemId || value.ID === null) {
        return false
    }
    return getInlineAttachmentRegex(itemId, fieldId).test(value.ID)
}

const getInlineAttachmentRegex = (itemId: number, fieldId: string): RegExp =>
    new RegExp(`^customfield.${itemId.toString()}.${fieldId}$`)

function init(store: AppStore, oldUIContext: IOldUIContext): OldUIInit {
    const makeDetailPageInlineAttachment = (
        itemId: number,
        customFieldDefinitionId: string,
        customFieldName: string,
        deleteAttachmentAllowed: boolean,
        addAttachmentAllowed: boolean,
        isPrintView: boolean,
    ): void => {
        const element = document.getElementById(
            "newui-inlineattachment" + customFieldDefinitionId,
        )

        store.dispatch(initialize(customFieldDefinitionId))

        if (element) {
            const root = createRoot(element)
            root.render(
                <Provider store={store}>
                    <OldUIContext.Provider value={oldUIContext}>
                        <OldUIContext.Consumer>
                            {(): JSX.Element => (
                                <DetailPageInlineAttachment
                                    itemId={itemId}
                                    customFieldDefinitionId={
                                        customFieldDefinitionId
                                    }
                                    customFieldName={customFieldName}
                                    canDeleteAttachments={
                                        deleteAttachmentAllowed
                                    }
                                    canAddAttachments={addAttachmentAllowed}
                                    isPrintView={isPrintView}
                                />
                            )}
                        </OldUIContext.Consumer>
                    </OldUIContext.Provider>
                </Provider>,
            )
        }
    }

    const makeCNIInlineAttachment = (
        ipfId: string,
        ipfName: string,
        ipfValue: File[],
    ): void => {
        const element = document.getElementById(
            "newui-inlineattachment" + ipfId,
        )

        store.dispatch(initialize(ipfId))

        if (element) {
            const root = createRoot(element)
            root.render(
                <Provider store={store}>
                    <OldUIContext.Provider value={oldUIContext}>
                        <OldUIContext.Consumer>
                            {(): JSX.Element => (
                                <CNIInlineAttachment
                                    ipfValue={ipfValue}
                                    ipfName={ipfName}
                                    customFieldDefinitionId={ipfId}
                                />
                            )}
                        </OldUIContext.Consumer>
                    </OldUIContext.Provider>
                </Provider>,
            )
        }
    }

    const updateElements = (values: IOldUIIPEField[]): void => {
        const {
            resourceHeader: {
                resource: { resourceType, id },
            },
            inlineAttachments: { attachmentFields },
        } = store.getState()
        if (resourceType == "Item" && !!id) {
            const fieldIds = Object.keys(attachmentFields)

            values.forEach((value) => {
                fieldIds.forEach((fieldKey) => {
                    if (isInlineAttachmentValue(id, fieldKey, value)) {
                        store.dispatch(requestAttachments(id, fieldKey))
                    }
                })
            })
        }
    }

    const loadMoreAttachments = (customFieldDefinitionId: string): void => {
        const {
            inlineAttachments: { attachmentFields },
        } = store.getState()

        const fieldIds = Object.keys(attachmentFields)

        if (fieldIds.includes(customFieldDefinitionId)) {
            store.dispatch(loadMoreAttachmentsAction(customFieldDefinitionId))
        }
    }

    return {
        makeDetailPageInlineAttachment,
        makeCNIInlineAttachment,
        updateElements,
        loadMoreAttachments,
    }
}

export default init
